// Core engine types. Squares are 0–63 in FEN order: a8 = 0, h8 = 7, a1 = 56,
// h1 = 63, so row 0 is rank 8. Pieces are signed codes in an Int8Array —
// positive white, negative black, 0 empty — which keeps a position a flat
// 64-byte array that copies cheaply and crosses a Worker boundary as-is.

export type Color = 'w' | 'b';

/** Square index 0–63 (a8 = 0 … h1 = 63). */
export type Square = number;

/** Absent square (no en-passant target). */
export const NO_SQUARE = -1;

export const EMPTY = 0;
export const PAWN = 1;
export const KNIGHT = 2;
export const BISHOP = 3;
export const ROOK = 4;
export const QUEEN = 5;
export const KING = 6;

/** Unsigned piece type, 1–6. */
export type PieceType = 1 | 2 | 3 | 4 | 5 | 6;

/** Piece types a pawn may promote to. */
export type PromotionType = 2 | 3 | 4 | 5;

export const PROMOTION_TYPES: readonly PromotionType[] = [QUEEN, ROOK, BISHOP, KNIGHT];

// Castling rights bitmask.
export const CASTLE_WK = 1;
export const CASTLE_WQ = 2;
export const CASTLE_BK = 4;
export const CASTLE_BQ = 8;

// Move flags bitmask.
export const FLAG_NONE = 0;
export const FLAG_CAPTURE = 1;
export const FLAG_DOUBLE_PUSH = 2;
export const FLAG_EN_PASSANT = 4;
export const FLAG_CASTLE_KING = 8;
export const FLAG_CASTLE_QUEEN = 16;
export const FLAG_PROMOTION = 32;

export interface Move {
  readonly from: Square;
  readonly to: Square;
  /** Signed code of the moving piece. */
  readonly piece: number;
  /** Signed code of the captured piece, or EMPTY. For en passant, the captured pawn. */
  readonly captured: number;
  /** Promotion piece type, or EMPTY. */
  readonly promotion: number;
  readonly flags: number;
}

export interface Position {
  readonly board: Int8Array;
  readonly turn: Color;
  readonly castling: number;
  /** Square a pawn just skipped over, capturable en passant; NO_SQUARE otherwise. */
  readonly epSquare: Square;
  /** Plies since the last capture or pawn move (50-move rule). */
  readonly halfmoveClock: number;
  readonly fullmoveNumber: number;
}

export function colorOf(code: number): Color {
  return code > 0 ? 'w' : 'b';
}

export function typeOf(code: number): number {
  return code < 0 ? -code : code;
}

export function opposite(color: Color): Color {
  return color === 'w' ? 'b' : 'w';
}

/** Signed code for a piece type of a color. */
export function pieceCode(type: number, color: Color): number {
  return color === 'w' ? type : -type;
}

export function isColor(code: number, color: Color): boolean {
  return color === 'w' ? code > 0 : code < 0;
}
